import React, { useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import {
  Box, Button, Chip, CircularProgress, FormControl, InputLabel, MenuItem,
  Select, Typography
} from '@material-ui/core'
import RefreshIcon from '@material-ui/icons/Refresh'
import WarningIcon from '@material-ui/icons/Warning'
import PhotoCameraIcon from '@material-ui/icons/PhotoCamera'
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import SmsIcon from '@material-ui/icons/Sms'
import PhoneIcon from '@material-ui/icons/Phone'
import { useV3Styles } from './v3Styles'

const activityMeta = {
  issue: { label: 'Issue', color: '#c62828', bg: '#ffebee', icon: WarningIcon },
  photo: { label: 'Photo', color: '#1565c0', bg: '#e3f2fd', icon: PhotoCameraIcon },
  message: { label: 'Message', color: '#5d4037', bg: '#efebe9', icon: ChatBubbleOutlineIcon },
  completion: { label: 'Complete', color: '#2e7d32', bg: '#e8f5e9', icon: CheckCircleOutlineIcon },
  alert: { label: 'Alert', color: '#e65100', bg: '#fff3e0', icon: WarningIcon },
  sms: { label: 'SMS', color: '#00695c', bg: '#e0f2f1', icon: SmsIcon },
  call: { label: 'Phone Call', color: '#283593', bg: '#e8eaf6', icon: PhoneIcon },
  system: { label: 'Field Update', color: '#546e7a', bg: '#eceff1', icon: ChatBubbleOutlineIcon }
}

const formatActivityTime = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const diffMs = Date.now() - date.getTime()
  const diffMinutes = Math.floor(diffMs / 60000)
  if (diffMinutes < 1) return 'Just now'
  if (diffMinutes < 60) return `${diffMinutes}m ago`
  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const trimBody = (body = '', maxLength = 130) => {
  const text = String(body || '').replace(/\s+/g, ' ').trim()
  if (text.length <= maxLength) return text
  return `${text.slice(0, maxLength - 3)}...`
}

export const FieldActivityMiniList = ({ activities, loading, emptyText, onSelectSite, compact = false }) => {
  const classes = useV3Styles()

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={compact ? 1 : 3}>
        <CircularProgress size={18} />
      </Box>
    )
  }

  if (!activities.length) {
    return (
      <Typography variant="body2" color="textSecondary" style={{ fontSize: 12, padding: compact ? '4px 0' : 12 }}>
        {emptyText || 'No field activity yet.'}
      </Typography>
    )
  }

  return (
    <div>
      {activities.map(activity => {
        const meta = activityMeta[activity.kind] || activityMeta.message
        const Icon = meta.icon
        const photoUrls = activity.photoUrls || []
        return (
          <div
            key={activity._id}
            className={compact ? classes.activityItemCompact : classes.activityItem}
            onClick={() => onSelectSite && activity.scheduleItemId && onSelectSite(activity.scheduleItemId, activity)}
          >
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" style={{ gap: 8 }}>
              <Box display="flex" style={{ gap: 8 }} alignItems="flex-start">
                <span className={classes.activityIcon} style={{ backgroundColor: meta.bg, color: meta.color }}>
                  <Icon style={{ fontSize: 14 }} />
                </span>
                <div>
                  <Typography variant="body2" className={classes.activityTitle}>
                    {activity.title}
                  </Typography>
                  {!compact && (
                    <Typography variant="caption" className={classes.activitySite}>
                      {activity.siteName || 'Unknown site'}
                    </Typography>
                  )}
                </div>
              </Box>
              <Chip
                size="small"
                label={activity.categoryLabel || meta.label}
                style={{ backgroundColor: meta.bg, color: meta.color, height: 18, fontSize: 9, fontWeight: 700 }}
              />
            </Box>

            {activity.body && (
              <Typography variant="body2" className={classes.activityBody}>
                {trimBody(activity.body, compact ? 110 : 150)}
              </Typography>
            )}

            {photoUrls.length > 0 && (
              <div className={classes.activityPhotos}>
                {photoUrls.slice(0, compact ? 3 : 4).map(url => (
                  <img key={url} src={url} alt="Field upload" className={classes.activityPhoto} />
                ))}
                {photoUrls.length > (compact ? 3 : 4) && (
                  <span className={classes.activityPhotoMore}>+{photoUrls.length - (compact ? 3 : 4)}</span>
                )}
              </div>
            )}

            <Typography variant="caption" className={classes.activityMeta}>
              {[activity.authorName, formatActivityTime(activity.createdAt)].filter(Boolean).join(' - ')}
            </Typography>
          </div>
        )
      })}
    </div>
  )
}

const FieldActivityPanel = ({ url, selectedId, selectedItem, onSelectSite }) => {
  const classes = useV3Styles()
  const [activities, setActivities] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [typeFilter, setTypeFilter] = useState('all')
  const [scopeMode, setScopeMode] = useState('global')

  const fetchActivity = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true)
    setError('')
    try {
      const baseParams = { limit: 50, type: typeFilter }
      if (selectedId) {
        const scopedResp = await axios.get(`${url}/api/herbicide-schedule/activity`, {
          params: { ...baseParams, scheduleItemId: selectedId }
        })
        const scopedItems = scopedResp.data?.items || []
        if (scopedItems.length) {
          setActivities(scopedItems)
          setScopeMode('site')
          return
        }
      }

      const resp = await axios.get(`${url}/api/herbicide-schedule/activity`, { params: baseParams })
      setActivities(resp.data?.items || [])
      setScopeMode(selectedId ? 'global_fallback' : 'global')
    } catch (err) {
      console.error('Error fetching field activity:', err)
      setError('Unable to load field activity.')
    } finally {
      if (!silent) setLoading(false)
    }
  }, [selectedId, typeFilter, url])

  useEffect(() => {
    fetchActivity()
  }, [fetchActivity])

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      fetchActivity({ silent: true })
    }, 45000)
    return () => window.clearInterval(intervalId)
  }, [fetchActivity])

  return (
    <aside className={classes.activityPanel}>
      <div className={classes.activityPanelHeader}>
        <div>
          <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#263238' }}>
            {scopeMode === 'site' ? 'Site Activity' : 'Field Activity'}
          </Typography>
          {selectedItem?.siteName && (
            <Typography variant="caption" style={{ color: '#78909c', display: 'block', lineHeight: 1.1 }}>
              {scopeMode === 'site' ? selectedItem.siteName : scopeMode === 'global_fallback' ? 'No site activity. Showing recent global activity.' : 'Recent global activity'}
            </Typography>
          )}
        </div>
        <Button size="small" onClick={() => fetchActivity()} disabled={loading} style={{ minWidth: 32 }}>
          {loading ? <CircularProgress size={14} /> : <RefreshIcon style={{ fontSize: 17 }} />}
        </Button>
      </div>

      <div className={classes.activityPanelControls}>
        <FormControl variant="outlined" size="small" fullWidth>
          <InputLabel>Signal</InputLabel>
          <Select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)} label="Signal">
            <MenuItem value="all">All Activity</MenuItem>
            <MenuItem value="issue">Issues</MenuItem>
            <MenuItem value="photo">Photos</MenuItem>
            <MenuItem value="message">Messages</MenuItem>
            <MenuItem value="system">Calls / SMS / Updates</MenuItem>
          </Select>
        </FormControl>
      </div>

      {selectedId && scopeMode === 'site' && (
        <Typography variant="caption" className={classes.activityPanelHint}>
          Showing activity for the selected site. Selecting a feed item jumps the editor to that site.
        </Typography>
      )}

      {error ? (
        <Typography variant="body2" color="error" style={{ fontSize: 12, padding: 12 }}>{error}</Typography>
      ) : (
        <FieldActivityMiniList
          activities={activities}
          loading={loading}
          emptyText={selectedId ? 'No site or global subcontractor activity has been posted yet.' : 'No subcontractor field activity has been posted yet.'}
          onSelectSite={onSelectSite}
        />
      )}
    </aside>
  )
}

export default FieldActivityPanel
